import { Copy, Check, Volume2, CircleStop, Pencil, RotateCcw } from 'lucide-react'
import { ChatMessage } from '../../../../types'
import {
  useActiveChatId,
  useChatMessages,
  useDevMode,
  useIsChatQueueSettled,
  useLatestFact,
  useMarkFactAsProcessed,
} from '../../../../store'
import { useActions } from '../../../../providers'
import { memo, useState, isValidElement, useEffect, useMemo } from 'react'
import { motion, AnimatePresence, Variants } from 'framer-motion'
import { useTimeout } from '../../../../hooks'
import { MemoryButton } from '../MemoryButton'
import { ChatShareButton } from '../ChatShareButton'
import { twMerge } from 'tailwind-merge'
import { Button, ActionPopover, Tooltip } from '@highlight/ui'
import { MessageFeedback } from '@renderer/components/feedback'

const iconVariants = {
  initial: { scale: 0, opacity: 0 },
  enter: { scale: 1, opacity: 1 },
  exit: { scale: 0, opacity: 0 },
} as Variants

// Controls the staggered animation for the individual icons
const iconTransition = { duration: 0.08 }

interface MessageActionsProps {
  message: ChatMessage
  isMostRecent: boolean
  /** A live turn on this chat disables the developer redo. */
  isTurnActive: boolean
  allowUserEditing?: boolean
  editMessage: (message: ChatMessage) => void
  onRedoTurn: () => Promise<void>
}

export const MessageActions = memo(
  ({ message, isMostRecent, isTurnActive, allowUserEditing = true, editMessage, onRedoTurn }: MessageActionsProps) => {
    const { copyTextToClipboard, handleSpeaking, isSpeaking } = useActions()
    const activeChatId = useActiveChatId()
    const messages = useChatMessages(activeChatId)
    const isSpeakingThisMessage = isSpeaking === message.id
    // Hold actions hidden while the queued-messages panel is mounting or
    // collapsing — keeps the icon-scale entry animation from competing with the
    // panel exit / bottom-anchor scroll. Only the most-recent message actually
    // observes the gate; passing undefined for everything else avoids the per-
    // message Jotai subscription churn during queue transitions.
    const isQueueSettled = useIsChatQueueSettled(isMostRecent ? activeChatId : undefined)

    // Dev mode only decides whether the redo button is drawn. The endpoint
    // behind it runs its own admin gate. The redo callback arrives as a prop:
    // building it here would subscribe every message row to the submit flow,
    // which republishes on each streaming token.
    const devMode = useDevMode()
    const [redoing, setRedoing] = useState(false)

    const timeout = useTimeout()
    const [copied, setCopied] = useState(false)

    const [realtimeProfileText, setRealtimeProfileText] = useState<string | null>(null)
    const [autoUpdatedMemory, setAutoUpdatedMemory] = useState(false)

    const latestFact = useLatestFact()
    const markFactAsProcessed = useMarkFactAsProcessed()

    // Calculate message IDs to share - include current message and previous tool/assistant messages
    const messageIdsToShare = useMemo(() => {
      if (!messages || messages.length === 0) {
        return [message.id]
      }

      // Find the current message index
      const currentMessageIndex = messages.findIndex((msg) => msg.id === message.id)
      if (currentMessageIndex === -1) {
        return [message.id]
      }

      const messageIds: string[] = [message.id]

      // Look backwards from current message to collect consecutive tool/assistant messages
      for (let i = currentMessageIndex - 1; i >= 0; i--) {
        const prevMessage = messages[i]

        // Stop if we hit a user message
        if (prevMessage.role === 'user') {
          break
        }

        // Include if it's a tool or assistant message
        if (prevMessage.role === 'tool' || prevMessage.role === 'assistant') {
          messageIds.unshift(prevMessage.id) // Add to beginning to maintain chronological order
        } else {
          // Stop if we hit any other role
          break
        }
      }

      return messageIds
    }, [messages, message.id])

    useEffect(() => {
      if (message.role === 'assistant' && isMostRecent && latestFact && !latestFact.processed) {
        setRealtimeProfileText(latestFact.text)
        setAutoUpdatedMemory(true)

        window.ampltd.trackEvent('Chat Message', {
          action: 'Memory Detected',
        })

        markFactAsProcessed()
      }
    }, [message.role, message.id, isMostRecent, latestFact, markFactAsProcessed])

    const getMessageContent = (content: string | React.ReactNode | undefined): string | undefined => {
      if (typeof content === 'string') return content
      if (isValidElement(content)) return content.props.children
      return undefined
    }

    const onCopyText = () => {
      const content = getMessageContent(message.content)
      if (!content) return

      window.ampltd.trackEvent('Chat Message', {
        action: 'Copy',
      })

      copyTextToClipboard(message.content as string, true)
      setCopied(true)
      timeout(() => {
        setCopied(false)
      }, 2000)
    }

    const onSpeak = async () => {
      if (typeof handleSpeaking !== 'function') return

      const content = getMessageContent(message.content)
      if (!content) return

      window.ampltd.trackEvent('Chat Message', {
        action: 'Speak',
      })

      await handleSpeaking(content || '', message.id)
    }

    const onRedo = async () => {
      if (redoing) return

      window.ampltd.trackEvent('Chat Message', {
        action: 'Redo Turn',
      })

      setRedoing(true)
      try {
        await onRedoTurn()
      } finally {
        setRedoing(false)
      }
    }

    if (message.content === null || message.content === undefined || typeof message.content !== 'string') {
      return <div />
    }

    // Bail entirely for roles or messages that can never show actions — keeps
    // the row from reserving height in those cases.
    const isRoleEligible = message.role === 'assistant' || message.role === 'user'
    if (!isRoleEligible || message.error) {
      return <div />
    }

    // Wrapper is always mounted so the action row height is reserved from
    // the start. Only the most-recent message's visibility is gated on
    // isQueueSettled — older messages keep their normal hover-reveal during
    // queue transitions so the user can still copy/edit them.
    const showActions = !isMostRecent || isQueueSettled

    return (
      <div
        className={twMerge(
          'transition-opacity duration-150',
          showActions
            ? twMerge(
                'opacity-0 group-hover/message:opacity-100',
                (isMostRecent || isSpeakingThisMessage) && 'opacity-100',
              )
            : 'pointer-events-none opacity-0',
        )}
      >
        <ActionPopover.Popup className="gap-1.5 overflow-visible rounded-none border-none bg-transparent p-0 shadow-none">
          <Tooltip.Provider>
            {message.role === 'assistant' && realtimeProfileText && (
              <MemoryButton fact={realtimeProfileText} autoUpdated={autoUpdatedMemory} />
            )}

            {message.role === 'user' && (
              <>
                <Tooltip>
                  <Tooltip.Trigger render={<Button size="sm" shape="square" variant="ghost" onClick={onCopyText} />}>
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.div
                        key={copied ? 'checkmark' : 'copy'}
                        className="flex items-center justify-center"
                        initial="initial"
                        animate="enter"
                        exit="exit"
                        variants={iconVariants}
                        transition={iconTransition}
                      >
                        {copied ? <Check size={16} /> : <Copy size={16} />}
                      </motion.div>
                    </AnimatePresence>
                  </Tooltip.Trigger>
                  <Tooltip.Content sideOffset={2}>Copy message to clipboard</Tooltip.Content>
                </Tooltip>
                {allowUserEditing && (
                  <Tooltip>
                    <Tooltip.Trigger
                      render={
                        <Button
                          size="sm"
                          shape="square"
                          variant="ghost"
                          onClick={() => {
                            window.ampltd.trackEvent('Chat Message', {
                              location: 'user message',
                              action: 'Edit',
                            })
                            editMessage(message)
                          }}
                          aria-label="Edit message"
                        />
                      }
                    >
                      <AnimatePresence mode="wait" initial={false}>
                        <motion.div
                          key="edit-message"
                          initial="initial"
                          animate="enter"
                          exit="exit"
                          variants={iconVariants}
                          transition={iconTransition}
                          className="flex items-center justify-center"
                        >
                          <Pencil size={16} />
                        </motion.div>
                      </AnimatePresence>
                    </Tooltip.Trigger>
                    <Tooltip.Content sideOffset={2}>Edit message</Tooltip.Content>
                  </Tooltip>
                )}
              </>
            )}

            {message.role === 'assistant' && (
              <>
                {!message.error && (
                  <Tooltip>
                    <Tooltip.Trigger render={<Button size="sm" shape="square" variant="ghost" onClick={onCopyText} />}>
                      <AnimatePresence mode="wait" initial={false}>
                        <motion.div
                          key={copied ? 'checkmark' : 'copy'}
                          className="flex items-center justify-center"
                          initial="initial"
                          animate="enter"
                          exit="exit"
                          variants={iconVariants}
                          transition={iconTransition}
                        >
                          {copied ? <Check size={16} /> : <Copy size={16} />}
                        </motion.div>
                      </AnimatePresence>
                    </Tooltip.Trigger>
                    <Tooltip.Content sideOffset={2}>Copy message to clipboard</Tooltip.Content>
                  </Tooltip>
                )}

                {!message.error && typeof handleSpeaking === 'function' && (
                  <Tooltip>
                    <Tooltip.Trigger render={<Button size="sm" shape="square" variant="ghost" onClick={onSpeak} />}>
                      <AnimatePresence mode="wait" initial={false}>
                        <motion.div
                          key={isSpeakingThisMessage ? 'stop' : 'speak'}
                          initial="initial"
                          animate="enter"
                          exit="exit"
                          variants={iconVariants}
                          transition={iconTransition}
                          className="flex items-center justify-center"
                        >
                          {isSpeakingThisMessage ? <CircleStop size={16} /> : <Volume2 size={16} />}
                        </motion.div>
                      </AnimatePresence>
                    </Tooltip.Trigger>
                    <Tooltip.Content sideOffset={2}>
                      {isSpeakingThisMessage ? 'Stop speaking' : 'Speak message aloud'}
                    </Tooltip.Content>
                  </Tooltip>
                )}

                {devMode && !message.error && (
                  <Tooltip>
                    <Tooltip.Trigger render={<div className="relative flex" />}>
                      <Button
                        size="sm"
                        shape="square"
                        variant="ghost"
                        onClick={onRedo}
                        disabled={redoing || isTurnActive}
                        aria-label="Redo turn"
                      >
                        <AnimatePresence mode="wait" initial={false}>
                          <motion.div
                            key="redo-turn"
                            initial="initial"
                            animate="enter"
                            exit="exit"
                            variants={iconVariants}
                            transition={iconTransition}
                            className="flex items-center justify-center"
                          >
                            <RotateCcw size={16} className={redoing ? 'animate-spin' : undefined} />
                          </motion.div>
                        </AnimatePresence>
                      </Button>
                    </Tooltip.Trigger>
                    <Tooltip.Content sideOffset={2}>Re-run this turn (developer)</Tooltip.Content>
                  </Tooltip>
                )}

                {/* Share Button - only for assistant messages */}
                <motion.div initial="hidden" animate="show" exit="exit" key="share-button">
                  <ChatShareButton messageIds={messageIdsToShare} />
                </motion.div>

                {!message.error && message.id && message.role === 'assistant' && (
                  <MessageFeedback
                    subject={{ messageId: message.id, conversationId: message.conversation_id }}
                    source="chat-message"
                    initialRating={message.given_feedback as 'positive' | 'negative' | null}
                  />
                )}
              </>
            )}
          </Tooltip.Provider>
        </ActionPopover.Popup>
      </div>
    )
  },
)
MessageActions.displayName = 'MessageActions'
